/**
 * Signing in, on every platform (`02-api-and-data.md` section 4).
 *
 * The web uses a cookie set by the API. The apps get a one-time ticket and store the session
 * token in the OS keychain, which is what `restoreSession` reads at startup.
 */

import { api, appSignInUrl, setBearerToken, webSignInUrl } from '../api/client.js';
import { isNative, platform } from '../platform/index.js';
import { native } from '../platform/tauri.js';

/** Loads the stored session token, so the first request after launch is already signed in. */
export async function restoreSession(): Promise<boolean> {
  if (!isNative) return true;
  try {
    const token = await native.authGetToken();
    setBearerToken(token);
    return Boolean(token);
  } catch {
    return false;
  }
}

/**
 * Starts sign in.
 *
 * On the web this is a redirect. In the apps the native side opens the system browser, waits for
 * the deep link or the loopback callback, exchanges the ticket and stores the token, so the
 * frontend only has to refresh afterwards.
 */
export async function signIn(returnTo = '/'): Promise<void> {
  if (!isNative) {
    window.location.href = webSignInUrl(returnTo);
    return;
  }

  // The native side appends its own `app_nonce` and the loopback port it is listening on.
  const url = appSignInUrl({ platform: platform as 'windows' | 'linux' | 'android' });

  await native.authStart(url);
  const token = await native.authGetToken();
  setBearerToken(token);
}

export async function signOut(): Promise<void> {
  try {
    await api.post('/auth/logout');
  } catch {
    // Signing out locally matters more than telling the server.
  }
  if (isNative) {
    await native.authClear();
    setBearerToken(null);
  } else {
    window.location.href = '/';
  }
}

export async function signOutEverywhere(): Promise<void> {
  await api.post('/auth/logout-all');
  if (isNative) {
    await native.authClear();
    setBearerToken(null);
  } else {
    window.location.href = '/';
  }
}

/**
 * Opens the web app already signed in, which is how checkout works from the apps.
 *
 * Payment always happens on goodbuild.cc: the apps never embed a checkout
 * (`01-product-rules.md` section 10).
 */
export async function openWebHandoff(returnTo: string): Promise<void> {
  const { url } = await api.post<{ url: string }>('/auth/web-handoff', { return_to: returnTo });
  await openExternal(url);
}

/** Opens a link outside the app: the system browser on native, a new tab on the web. */
export async function openExternal(url: string): Promise<void> {
  if (isNative) {
    const { openUrl } = await import('@tauri-apps/plugin-opener');
    await openUrl(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}
